import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import QueenIcon from "@/assets/icons/bees/queen-icon.tsx"
import AddCircleIcon from "@/assets/icons/misc/add-circle-icon.tsx"
import CloseCrossIcon from "@/assets/icons/misc/close-cross-icon.tsx"
import RemoveCircleIcon from "@/assets/icons/misc/remove-circle-icon.tsx"
import CircleTickIcon from "@/assets/icons/task/circle-tick-icon.tsx"
import { type RenderFunctionDrawerItemComponentProps } from "@/components/render-function-drawer/render-function-drawer.tsx"
import { useGetHiveByIdQuery } from "@/features/bees/api/hives-api.ts"
import { useGetQueenByIdQuery } from "@/features/bees/api/queens-api.ts"
import {
  HIVES_LIST_VIEW,
  APIARIES_LIST_VIEW,
  QUEEN_VIEW,
} from "@/features/bees/routes/route-list.ts"
import { type QueenResponse } from "@/features/bees/types/queens.ts"
import styles from "@/features/tasks/components/associated-list/associated-list.module.css"
import { cn } from "@/utils/styles.ts"

export const AssociatedQueenCard: React.FC<
  RenderFunctionDrawerItemComponentProps
> = ({ id, onDelete, isSelected, onSelect }) => {
  const { data: queen } = useGetQueenByIdQuery(id)
  const { data: workingHive } = useGetHiveByIdQuery(queen?.hive as string, {
    skip: !queen?.hive,
  })
  const { t } = useTranslation()
  const navigate = useNavigate()

  const navigateToQueen = () => {
    if (queen?.apiary) {
      navigate(
        `${APIARIES_LIST_VIEW}/${queen?.apiary}${HIVES_LIST_VIEW}/${queen?.hive}/queen/${queen?.id}/`,
      )
    } else {
      navigate(`${QUEEN_VIEW}/${queen?.id}/`)
    }
  }

  const handleQueenClick = () => {
    onSelect && onSelect()
    !onSelect && navigateToQueen()
  }

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDelete && onDelete(id)
  }

  return (
    <div
      onClick={handleQueenClick}
      className={cn(
        "flex items-center justify-between gap-2 rounded-[10px] border border-gray-200 bg-[var(--light-grey)] p-2 text-neutral-700",
        isSelected && "border border-brown-500 bg-white",
      )}
    >
      <div
        className={cn(
          "grid w-[70%] grid-cols-[30px_auto] items-center gap-2",
          onSelect && "w-full grid-cols-[30px_30px_auto]",
        )}
      >
        {onSelect && (
          <>
            {isSelected ? (
              <CircleTickIcon />
            ) : (
              <div
                className={
                  "border-dark-grey box-border h-[24px] w-[24px] rounded-full border bg-[var(--background-color)] pr-3"
                }
              />
            )}
          </>
        )}
        <div
          className={cn(
            "grid h-[30px] w-[30px] place-content-center rounded-[4px] bg-gray-600 text-white opacity-60",
            isSelected && "bg-brown-500",
          )}
        >
          <QueenIcon />
        </div>
        <div className={"flex w-[70%] flex-col"}>
          <div className={"truncate text-[14px]"}>{queen?.name}</div>
          <div className={"truncate text-neutral-700"}>
            {t("theHive")} {workingHive?.code}
          </div>
        </div>
      </div>
      {onDelete && (
        <div onClick={handleDelete}>
          <CloseCrossIcon className={"text-neutral-500"} />
        </div>
      )}
    </div>
  )
}

interface AssociatedQueensListProps {
  queens: QueenResponse[]
  selectedQueensId: string[]
  onDelete: (queenId: string[]) => void
}

export const AssociatedQueensList: React.FC<AssociatedQueensListProps> = ({
  queens,
  selectedQueensId,
  onDelete,
}) => {
  const { t } = useTranslation()
  const selectedQueens = queens.filter((queen) =>
    selectedQueensId.includes(queen.id),
  )
  const [listLimit, setListLimit] = React.useState(3)
  const expandList = () => setListLimit(selectedQueens.length)
  const collapseList = () => setListLimit(3)
  const list = selectedQueens.slice(0, listLimit)

  const handleDelete = (queenId: string) => {
    const newQueens = selectedQueens.filter((queen) => queen.id !== queenId)
    onDelete(newQueens.map((q) => q.id))
  }

  return (
    <div className={"flex flex-col gap-1"}>
      {list.map((queen, index) => (
        <AssociatedQueenCard
          id={queen.id}
          key={index}
          onDelete={handleDelete}
        />
      ))}
      {selectedQueens.length > listLimit && (
        <i className={styles["more-items"]} onClick={expandList}>
          <AddCircleIcon size={20} /> {t("seeOtherHivesAssociated")}
        </i>
      )}
      {selectedQueens.length === listLimit && selectedQueensId.length > 3 && (
        <i className={styles["more-items"]} onClick={collapseList}>
          <RemoveCircleIcon size={20} /> {t("hideOtherHivesAssociated")}
        </i>
      )}
    </div>
  )
}
